import axios from "axios";

/**
 * Thin axios wrapper for the /admin/bdc/* and /admin/ad/* endpoints.
 * Parallels drApi.js (60s cache, cache-bust on writes). Callers get plain
 * promises that resolve to `response.data`.
 */

function apiBase() {
  const raw = process.env.REACT_APP_API || "";
  return raw.endsWith("/") ? raw : raw + "/";
}

function authHeaders() {
  const token = localStorage.getItem("accessToken");
  return token ? { "x-access-token": token } : {};
}

function url(path) {
  const clean = path.startsWith("/") ? path.slice(1) : path;
  return apiBase() + clean;
}

async function get(path, params, config) {
  const res = await axios.get(url(path), { headers: authHeaders(), params, ...config });
  return res.data;
}

async function post(path, body) {
  const res = await axios.post(url(path), body ?? {}, { headers: authHeaders() });
  return res.data;
}

async function put(path, body) {
  const res = await axios.put(url(path), body ?? {}, { headers: authHeaders() });
  return res.data;
}

export const bdcApi = {
  overview: () => get("admin/bdc/overview"),

  listLinks: (params) => get("admin/bdc/links", params),
  createLink: (body) => post("admin/bdc/links", body),

  listNetwork: (params) => get("admin/bdc/network", params),
  entityDetail: (entityType, entityId) => get(`admin/bdc/entities/${entityType}/${entityId}`),

  listClaims: (params) => get("admin/bdc/claims", params),
  createClaim: (body) => post("admin/bdc/claims", body),

  // Bulk import (clients + consultant invites). FLAT response bodies —
  // BulkImportWizard reads res.job_id / res.status / res.processed directly.
  createImport: (body) => post("admin/bdc/import", body),
  getImport: (jobId) => get(`admin/bdc/import/${jobId}`),

  listPayouts: (params) => get("admin/bdc/payouts", params),
  listCommissions: (params) => get("admin/bdc/commissions", params),

  leaderboards: (params) => get("admin/bdc/leaderboards", params),

  // AD-only.
  adListPendingClaims: (params) => get("admin/ad/claims/pending", params),
  adDecideClaim: (id, body) => post(`admin/ad/claims/${id}/decide`, body),
  adListAttributions: (params) => get("admin/ad/attribution", params),
  adOverrideAttribution: (id, body) => post(`admin/ad/attribution/${id}/override`, body),
  adApprovePayout: (id, body) => post(`admin/ad/payouts/${id}/approve`, body),
  adFreezePayout: (id, body) => post(`admin/ad/payouts/${id}/freeze`, body),
  adReleasePayout: (id, body) => post(`admin/ad/payouts/${id}/release`, body),
  adMarkPaid: (id, body) => post(`admin/ad/payouts/${id}/paid`, body),

  // AD — DR credential review (Phase D minimum).
  // `status` accepts PENDING | APPROVED | REJECTED | EXPIRED | all, and
  // comma-separated lists. Omitting it keeps the endpoint's PENDING default,
  // so existing callers are unchanged.
  adListPendingCredentials: (params) => get("admin/ad/dr/credentials/pending", params),
  adDecideCredential: (id, body) => post(`admin/ad/dr/credentials/${id}/decide`, body),

  // AD — manual-bank-transfer payment confirmation (v1 payment pivot).
  adListPendingPayments: (params) => get("admin/ad/payments/pending", params),
  adMarkInvestmentPaid: (investmentId, body) =>
    post(`admin/ad/investments/${investmentId}/mark-paid`, body),

  // AD — system-wide lists (Phase E §5.2 PayoutAdmin / SLABreachMonitor / AuditLogViewer).
  adListAllPayouts: (params) => get("admin/ad/payouts", params),
  adListSlaBreaches: (params) => get("admin/ad/sla/breaches", params),
  adListAuditLog: (params) => get("admin/ad/audit", params),

  // AD — email-log viewer (2026-05-08 fix). Mirrors audit log shape; resend
  // resets a SENT/FAILED/DEAD_LETTER row to QUEUED so the cron picks it up.
  adListEmailLog: (params, config) => get("admin/ad/email-log", params, config),
  adGetEmailLogFull: (id, config) => get(`admin/ad/email-log/${id}/full`, undefined, config),
  adResendEmail: (id) => post(`admin/ad/email-log/${id}/resend`),

  // AD — marketing campaigns (consultant-import WI-4). Compose → preview
  // audience count → send (DRAFT→ENQUEUED, one-shot) → delivery rollup
  // derived from email_logs.
  adCreateCampaign: (body) => post("admin/ad/marketing/campaigns", body),
  adListCampaigns: (params) => get("admin/ad/marketing/campaigns", params),
  adGetCampaign: (id) => get(`admin/ad/marketing/campaigns/${id}`),
  adCampaignPreviewCount: (id) => get(`admin/ad/marketing/campaigns/${id}/preview-count`),
  adSendCampaign: (id) => post(`admin/ad/marketing/campaigns/${id}/send`),
  adCampaignStatus: (id) => get(`admin/ad/marketing/campaigns/${id}/status`),

  // BDC — mint a single-use, email-bound consultant invitation (Phase 5).
  // Response data.invited ∈ EMAIL_SENT | EXISTING_ACCOUNT_QUEUED |
  // EXISTING_ACCOUNT_AWAITING_ACTIVATION. BDC-only (server-enforced).
  bdcInviteConsultant: (body) => post("admin/bdc/consultant-invitations", body),

  // AD — consultant invitation approval queue (Phase 5). Prospect/SponsoringBdc/
  // Invitation are hydrated on each row; decide with {decision, notes}.
  adListConsultantRequests: (params) => get("admin/ad/consultant-requests", params),
  adDecideConsultantRequest: (id, body) =>
    post(`admin/ad/consultant-requests/${id}/decide`, body),

  // Commission configuration (Phase 5). commission-config is the read model the
  // dashboards use for their cut labels; commission-rates is the AD editor.
  // BOTH may 404 while the API side ships in parallel — callers MUST
  // feature-detect and degrade gracefully (omit the number / show "not
  // available") rather than crash.
  commissionConfig: () => get("admin/commission-config"),
  adGetCommissionRates: (params) => get("admin/ad/commission-rates", params),
  adPutCommissionRates: (body) => put("admin/ad/commission-rates", body),
  adCreateCommissionOverride: (body) => post("admin/ad/commission-rates/overrides", body),
  adEndCommissionOverride: (id, body) => post(`admin/ad/commission-rates/overrides/${id}/end`, body),

  // AD — compliance report export. Fetches the CSV with the x-access-token
  // header (instead of exposing a raw link that can't be authenticated or
  // that would require embedding a token into the URL — H9 review). Returns
  // a Blob + filename so the caller can trigger a local download.
  adComplianceReportFetch: async (params) => {
    const res = await axios.get(url("admin/ad/compliance/report"), {
      headers: authHeaders(),
      params,
      responseType: "blob",
    });
    const dispo = res.headers["content-disposition"] || "";
    const m = dispo.match(/filename="?([^"]+)"?/i);
    const filename = m ? m[1] : `compliance-report-${Date.now()}.csv`;
    return { blob: res.data, filename };
  },
};
